'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────
interface RequestFormData {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  selectedPlan: string;
  serviceNeeded: string;
  projectDetails: string;
  paymentMethod: string;
}

interface ContactFormData {
  fullName: string;
  email: string;
  phone: string;
  company: string;
  serviceNeeded: string;
  message: string;
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const CheckIcon = () => (
  <svg className="w-4 h-4 text-[#0ea5e9] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const ArrowRight = ({ className = 'w-4 h-4' }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

// ─── Scroll helper ────────────────────────────────────────────────────────────
const scrollTo = (id: string) => {
  const el = document.getElementById(id);
  if (el) {
    const offset = 72;
    const top = el.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
};

// ─── Navbar ───────────────────────────────────────────────────────────────────
const NAV_SECTIONS = ['hero', 'about', 'services', 'pricing', 'careers', 'contact'];
const NAV_LABELS: Record<string, string> = {
  hero: 'Home', about: 'About', services: 'Services',
  pricing: 'Pricing', careers: 'Careers', contact: 'Contact',
};

function Navbar({ onGetStarted }: { onGetStarted: () => void }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [active, setActive] = useState('hero');

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 30);
      for (let i = NAV_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(NAV_SECTIONS[i]);
        if (el && window.scrollY >= el.offsetTop - 140) {
          setActive(NAV_SECTIONS[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const handleNav = (id: string) => {
    scrollTo(id);
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/96 backdrop-blur-xl shadow-[0_1px_24px_rgba(10,22,40,0.08)] border-b border-slate-100/80'
          : 'bg-transparent'
      }`}
      aria-label="Main navigation"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[72px]">
          {/* Logo */}
          <button
            onClick={() => handleNav('hero')}
            className="flex items-center gap-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] rounded-xl"
            aria-label="QUANTIVA IT — scroll to top"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#0369a1] flex items-center justify-center shadow-[0_2px_12px_rgba(14,165,233,0.35)]">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M9 2L15.5 5.5V12.5L9 16L2.5 12.5V5.5L9 2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                <path d="M9 6L12 7.5V10.5L9 12L6 10.5V7.5L9 6Z" fill="white"/>
              </svg>
            </div>
            <div className="leading-none">
              <span className={`block font-bold text-[15px] tracking-tight transition-colors ${scrolled ? 'text-[#0a1628]' : 'text-[#0a1628]'}`}>
                QUANTIVA<span className="text-[#0ea5e9]"> IT</span>
              </span>
              <span className="block text-[9px] font-semibold tracking-[0.18em] text-slate-400 uppercase mt-0.5">IT Services</span>
            </div>
          </button>

          {/* Desktop links */}
          <div className="hidden lg:flex items-center gap-0.5">
            {NAV_SECTIONS.map((id) => (
              <button
                key={id}
                onClick={() => handleNav(id)}
                className={`relative px-4 py-2 text-[13.5px] font-medium rounded-lg transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] ${
                  active === id
                    ? 'text-[#0ea5e9]'
                    : scrolled
                    ? 'text-slate-600 hover:text-[#0ea5e9] hover:bg-slate-50'
                    : 'text-slate-700 hover:text-[#0ea5e9] hover:bg-slate-50/60'
                }`}
              >
                {NAV_LABELS[id]}
                {active === id && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-[#0ea5e9] rounded-full" />
                )}
              </button>
            ))}
          </div>

          {/* CTA + hamburger */}
          <div className="flex items-center gap-3">
            <button
              onClick={onGetStarted}
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 bg-[#0ea5e9] text-white text-[13.5px] font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-[0_4px_20px_rgba(14,165,233,0.4)] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
            >
              Get Started
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-xl text-slate-600 hover:text-[#0ea5e9] hover:bg-slate-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9]"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              ) : (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" /></svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`lg:hidden overflow-hidden transition-all duration-300 ${mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-white border-t border-slate-100 shadow-xl px-4 py-4 flex flex-col gap-1">
          {NAV_SECTIONS.map((id) => (
            <button
              key={id}
              onClick={() => handleNav(id)}
              className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200 ${
                active === id
                  ? 'text-[#0ea5e9] bg-[#0ea5e9]/8 font-semibold'
                  : 'text-slate-600 hover:text-[#0ea5e9] hover:bg-slate-50'
              }`}
            >
              {NAV_LABELS[id]}
            </button>
          ))}
          <button
            onClick={() => { onGetStarted(); setMobileOpen(false); }}
            className="mt-2 w-full flex items-center justify-center gap-2 px-5 py-3 bg-[#0ea5e9] text-white text-sm font-semibold rounded-full hover:bg-[#0369a1] transition-all"
          >
            Get Started <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section id="hero" className="relative min-h-screen flex items-center bg-gradient-to-br from-[#f8fafc] via-[#f0f9ff] to-[#e0f2fe] overflow-hidden pt-[72px]" aria-label="Hero">
      {/* Background layers */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#0ea5e9]/12 to-[#06b6d4]/6 blur-3xl" />
        <div className="absolute bottom-0 -left-20 w-[400px] h-[400px] rounded-full bg-[#0ea5e9]/6 blur-3xl" />
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.025]" style={{backgroundImage: 'linear-gradient(#0ea5e9 1px, transparent 1px), linear-gradient(90deg, #0ea5e9 1px, transparent 1px)', backgroundSize: '60px 60px'}} />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 w-full">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left */}
          <div>
            <div className="flex flex-wrap gap-2.5 mb-7">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-semibold text-slate-700 shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#0ea5e9] animate-pulse" />
                IT SERVICES & CONSULTANCY
              </span>
              <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-600 shadow-sm">
                <svg className="w-3.5 h-3.5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                USA-Based
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-[#0a1628] leading-[1.1] tracking-tight mb-6">
              Technology Solutions<br />
              <span className="text-[#0ea5e9]">Built for Business</span><br />
              <span className="text-slate-500 text-3xl sm:text-4xl lg:text-[2.75rem]">Growth</span>
            </h1>

            <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-xl">
              QUANTIVA IT is a professional U.S.-based IT services and business consultancy firm delivering high-impact technology solutions, digital transformation strategies, and operational excellence for organizations across America.
            </p>

            <div className="flex flex-wrap gap-3 mb-8">
              {['No lock-in contracts', 'Dedicated support', 'On-time delivery'].map((t) => (
                <span key={t} className="flex items-center gap-1.5 text-sm text-slate-600">
                  <span className="w-5 h-5 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center text-[#0ea5e9] text-xs font-bold">✓</span>
                  {t}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap gap-4">
              <button
                onClick={onGetStarted}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-[0_8px_30px_rgba(14,165,233,0.4)] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
              >
                Get Started
                <ArrowRight />
              </button>
              <button
                onClick={() => scrollTo('services')}
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-slate-300 text-slate-700 font-semibold rounded-full transition-all duration-300 hover:border-[#0ea5e9] hover:text-[#0ea5e9] hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
              >
                Explore Services
                <ArrowRight />
              </button>
            </div>
          </div>

          {/* Right — visual card */}
          <div className="relative hidden lg:block">
            <div className="bg-white rounded-3xl shadow-[0_20px_60px_rgba(14,165,233,0.12)] border border-slate-100 overflow-hidden">
              <div className="bg-gradient-to-br from-[#0a1628] to-[#0f2035] p-8 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 rounded-full bg-[#0ea5e9]/10 -translate-y-1/2 translate-x-1/2" />
                <div className="relative">
                  <div className="flex items-center gap-2 mb-5">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#0ea5e9] animate-pulse" />
                    <span className="text-white/60 text-xs font-medium uppercase tracking-widest">Live Operations</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { value: '99%', label: 'Uptime SLA' },
                      { value: '24/7', label: 'Support' },
                      { value: '100%', label: 'Satisfaction' },
                      { value: 'USA', label: 'Based' },
                    ].map((s) => (
                      <div key={s.label} className="bg-white/8 rounded-2xl p-4 backdrop-blur-sm border border-white/5">
                        <div className="text-2xl font-bold text-white">{s.value}</div>
                        <div className="text-white/50 text-xs mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-slate-800">Systems Operational</div>
                    <div className="text-xs text-slate-500">All services running normally</div>
                  </div>
                  <div className="ml-auto w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
                </div>
                <div className="space-y-2.5">
                  {['IT Infrastructure', 'Cloud Services', 'Cybersecurity', 'Business Consulting'].map((s) => (
                    <div key={s} className="flex items-center gap-2 text-sm text-slate-600">
                      <CheckIcon />
                      {s}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl border border-slate-100 p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#06b6d4] flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <div>
                <div className="text-sm font-bold text-slate-800">Fast Deployment</div>
                <div className="text-xs text-slate-500">On-time, every time</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function AboutSection() {
  return (
    <section id="about" className="py-20 lg:py-28 bg-white" aria-label="About QUANTIVA IT">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
              <span className="w-8 h-px bg-[#0ea5e9]" />
              About Us
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight mb-6">
              Your trusted partner in{' '}
              <span className="text-[#0ea5e9]">technology</span>{' '}
              and business growth.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              QUANTIVA IT is a professional U.S.-based IT services and business &amp; IT consultancy company. We help organizations of all sizes improve their technology infrastructure, streamline operations, and accelerate digital transformation.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              From strategic IT consulting and managed services to cloud solutions and cybersecurity, we deliver practical, business-focused technology solutions that drive measurable results. Our team of experienced professionals is committed to understanding your unique challenges and delivering solutions that work.
            </p>
            <div className="grid grid-cols-2 gap-4 mb-8">
              {[
                { value: '100%', label: 'Client Satisfaction' },
                { value: '24/7', label: 'Support Available' },
                { value: 'USA', label: 'Headquartered' },
                { value: '99%', label: 'Uptime SLA' },
              ].map((s) => (
                <div key={s.label} className="bg-[#f8fafc] rounded-2xl p-5 border border-slate-100">
                  <div className="text-2xl font-bold text-[#0ea5e9] mb-1">{s.value}</div>
                  <div className="text-sm text-slate-600">{s.label}</div>
                </div>
              ))}
            </div>
            <button
              onClick={() => scrollTo('contact')}
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
            >
              Get In Touch
              <ArrowRight />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              {
                icon: <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" /></svg>,
                title: 'IT Infrastructure',
                desc: 'Design, deploy, and manage robust IT infrastructure tailored to your business needs.',
              },
              {
                icon: <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15a4.5 4.5 0 004.5 4.5H18a3.75 3.75 0 001.332-7.257 3 3 0 00-3.758-3.848 5.25 5.25 0 00-10.233 2.33A4.502 4.502 0 002.25 15z" /></svg>,
                title: 'Cloud Solutions',
                desc: 'Migrate, optimize, and manage cloud environments for maximum efficiency.',
              },
              {
                icon: <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>,
                title: 'Cybersecurity',
                desc: 'Protect your business with enterprise-grade security and proactive threat management.',
              },
              {
                icon: <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" /></svg>,
                title: 'IT Consulting',
                desc: 'Strategic technology advisory to align IT investments with business objectives.',
              },
            ].map((item) => (
              <div key={item.title} className="bg-[#f8fafc] rounded-2xl p-6 border border-slate-100 hover:border-[#0ea5e9]/30 hover:shadow-md transition-all duration-300 group">
                <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center mb-4 group-hover:bg-[#0ea5e9]/20 transition-colors">
                  {item.icon}
                </div>
                <h3 className="font-semibold text-[#0a1628] mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────
const SERVICES = [
  {
    num: '01',
    title: 'IT Infrastructure & Managed Services',
    desc: 'Design, deploy, and manage enterprise-grade IT infrastructure with proactive monitoring, maintenance, and 24/7 support to keep your business running at peak performance.',
    tags: ['Network Design & Management', 'Server Administration', 'Proactive Monitoring & Support'],
  },
  {
    num: '02',
    title: 'Cloud Solutions & Migration',
    desc: 'Accelerate your cloud journey with strategic migration planning, cloud architecture design, and ongoing optimization across AWS, Azure, and Google Cloud platforms.',
    tags: ['Cloud Migration Strategy', 'Multi-Cloud Architecture', 'Cost Optimization & Governance'],
  },
  {
    num: '03',
    title: 'Cybersecurity & Compliance',
    desc: 'Protect your organization with comprehensive cybersecurity solutions including threat detection, vulnerability assessments, incident response, and regulatory compliance management.',
    tags: ['Threat Detection & Response', 'Security Audits & Assessments', 'Compliance Management (HIPAA, SOC2)'],
  },
  {
    num: '04',
    title: 'Business & IT Consultancy',
    desc: 'Align technology investments with business goals through strategic IT consulting, digital transformation roadmaps, and technology advisory services tailored to your industry.',
    tags: ['Digital Transformation Strategy', 'IT Roadmap Development', 'Technology Vendor Selection'],
  },
  {
    num: '05',
    title: 'Software Development & Integration',
    desc: 'Build custom software solutions and seamlessly integrate disparate systems to automate workflows, improve efficiency, and deliver exceptional user experiences.',
    tags: ['Custom Application Development', 'API & Systems Integration', 'Workflow Automation'],
  },
  {
    num: '06',
    title: 'Data Management & Analytics',
    desc: 'Transform raw data into actionable business intelligence with data architecture design, analytics platforms, and reporting solutions that drive informed decision-making.',
    tags: ['Data Architecture & Warehousing', 'Business Intelligence Dashboards', 'Data Security & Governance'],
  },
];

function ServiceCard({ service, onGetStarted }: { service: typeof SERVICES[0]; onGetStarted: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`group bg-white rounded-2xl border transition-all duration-300 overflow-hidden ${open ? 'border-[#0ea5e9]/40 shadow-lg' : 'border-slate-100 hover:border-[#0ea5e9]/30 hover:shadow-md'}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-4 p-6 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-inset rounded-2xl"
        aria-expanded={open}
      >
        <span className="text-xs font-bold text-[#0ea5e9] w-8 flex-shrink-0">{service.num}</span>
        <div className="w-10 h-10 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0ea5e9]/20 transition-colors">
          <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1 1 .03 2.7-1.388 2.43l-1.552-.31m-14.524.621l-1.552.31c-1.418.27-2.388-1.43-1.388-2.43L5 14.5" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[#0a1628] text-base">{service.title}</h3>
          {!open && <p className="text-sm text-slate-500 mt-0.5 truncate">{service.desc}</p>}
        </div>
        <svg className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="px-6 pb-6">
          <p className="text-slate-600 leading-relaxed mb-4 ml-[4.5rem]">{service.desc}</p>
          <div className="flex flex-wrap gap-2 ml-[4.5rem] mb-4">
            {service.tags.map((tag) => (
              <span key={tag} className="inline-flex items-center gap-1.5 text-xs text-slate-600 bg-[#f8fafc] border border-slate-200 rounded-full px-3 py-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9]" />
                {tag}
              </span>
            ))}
          </div>
          <div className="ml-[4.5rem]">
            <button onClick={onGetStarted} className="inline-flex items-center gap-2 text-sm font-semibold text-[#0ea5e9] hover:text-[#0369a1] transition-colors">
              Get Started <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ServicesSection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section id="services" className="py-20 lg:py-28 bg-[#f8fafc]" aria-label="Services">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-12">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
              <span className="w-8 h-px bg-[#0ea5e9]" />
              Our Solutions
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight">
              Everything you need to{' '}
              <span className="text-[#0ea5e9]">power</span>{' '}
              your IT.
            </h2>
          </div>
          <p className="text-slate-600 max-w-md lg:text-right">
            End-to-end IT services and consultancy that scale your business, secure your operations, and accelerate digital transformation.
          </p>
        </div>
        <div className="space-y-3">
          {SERVICES.map((s) => <ServiceCard key={s.num} service={s} onGetStarted={onGetStarted} />)}
        </div>
        <div className="mt-10 text-center">
          <button
            onClick={() => scrollTo('contact')}
            className="inline-flex items-center gap-2 px-7 py-3.5 border-2 border-[#0ea5e9] text-[#0ea5e9] font-semibold rounded-full transition-all duration-300 hover:bg-[#0ea5e9] hover:text-white hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
          >
            Discuss Your Requirements
            <ArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Why Us ───────────────────────────────────────────────────────────────────
function WhyUsSection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section id="why-us" className="py-20 lg:py-28 bg-white" aria-label="Why Choose QUANTIVA IT">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
            <span className="w-8 h-px bg-[#0ea5e9]" />
            Why Choose Us
            <span className="w-8 h-px bg-[#0ea5e9]" />
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight">
            Engineered for{' '}
            <span className="text-[#0ea5e9]">performance</span>{' '}
            and reliability.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            We deliver technology solutions that are technically sound and built to solve your toughest business challenges.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          {/* Featured */}
          <div className="lg:col-span-1 bg-gradient-to-br from-[#0a1628] to-[#0f2035] rounded-3xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-[#0ea5e9]/10 -translate-y-1/2 translate-x-1/2" />
            <div className="relative">
              <div className="w-12 h-12 rounded-2xl bg-[#0ea5e9]/20 flex items-center justify-center mb-6">
                <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
              </div>
              <span className="text-xs font-bold text-[#0ea5e9] uppercase tracking-widest mb-3 block">01</span>
              <h3 className="text-xl font-bold mb-3">Deep Technology Expertise</h3>
              <p className="text-white/70 text-sm leading-relaxed">Our team brings hands-on experience across the full technology stack — from infrastructure and cloud to cybersecurity and custom software development.</p>
              <div className="mt-6 pt-6 border-t border-white/10">
                <div className="text-2xl font-bold text-[#0ea5e9]">100%</div>
                <div className="text-white/60 text-xs mt-1">Certified professionals</div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4">
            {[
              { num: '02', title: 'Business-Focused Solutions', desc: 'Every technology recommendation is grounded in business value. We align IT investments with your strategic goals to maximize ROI.', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941" /></svg> },
              { num: '03', title: 'Strategic IT Consulting', desc: 'Benefit from senior-level technology advisory services that help you navigate complex IT decisions with confidence and clarity.', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5" /></svg> },
              { num: '04', title: 'Scalable Solutions', desc: 'We design solutions that grow with your business — from startup to enterprise — without costly re-architecture down the road.', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" /></svg> },
              { num: '05', title: 'Security & Compliance First', desc: 'Security is embedded in everything we do. We ensure your systems are protected and compliant with industry regulations from day one.', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg> },
            ].map((item) => (
              <div key={item.num} className="bg-[#f8fafc] rounded-2xl p-6 border border-slate-100 hover:border-[#0ea5e9]/30 hover:shadow-md transition-all duration-300 group">
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-xs font-bold text-[#0ea5e9]">{item.num}</span>
                  <div className="w-9 h-9 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center group-hover:bg-[#0ea5e9]/20 transition-colors">
                    {item.icon}
                  </div>
                </div>
                <h3 className="font-semibold text-[#0a1628] mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[#f8fafc] rounded-2xl border border-slate-100 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
            </div>
            <div>
              <h4 className="font-semibold text-[#0a1628]">100% Quality Assurance</h4>
              <p className="text-sm text-slate-500">Every engagement undergoes rigorous review. We leave no detail unchecked.</p>
            </div>
          </div>
          <button
            onClick={() => scrollTo('contact')}
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
          >
            Start Your Journey
            <ArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Process ──────────────────────────────────────────────────────────────────
const PROCESS_STEPS = [
  { num: '01', title: 'Consultation', desc: 'We begin with a thorough discovery session to understand your business objectives, current technology environment, pain points, and growth goals.' },
  { num: '02', title: 'Assessment', desc: 'Our team conducts a comprehensive evaluation of your existing IT infrastructure, systems, security posture, and operational workflows to identify gaps and opportunities.' },
  { num: '03', title: 'Strategy', desc: 'We develop a practical, prioritized technology roadmap and solution blueprint tailored to your business needs, budget, and timeline.' },
  { num: '04', title: 'Implementation', desc: 'Our certified professionals deploy the agreed solutions with minimal disruption to your operations, following industry best practices and quality standards.' },
  { num: '05', title: 'Support', desc: 'We provide ongoing monitoring, maintenance, and support to ensure your technology continues to perform optimally and evolve with your business.' },
];

function ProcessSection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section id="process" className="py-20 lg:py-28 bg-[#f8fafc]" aria-label="Our Process">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
            <span className="w-8 h-px bg-[#0ea5e9]" />
            The Process
            <span className="w-8 h-px bg-[#0ea5e9]" />
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight">
            From vision to{' '}
            <span className="text-[#0ea5e9]">reality</span>{' '}
            in five steps.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Our structured approach ensures transparency, efficiency, and exceptional results for every client engagement.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
          {PROCESS_STEPS.map((step) => (
            <div key={step.num} className="bg-white rounded-2xl border border-slate-100 p-6 hover:border-[#0ea5e9]/40 hover:shadow-lg transition-all duration-300 group">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#0ea5e9] to-[#0369a1] flex items-center justify-center mb-4 shadow-md group-hover:shadow-[0_4px_16px_rgba(14,165,233,0.3)] transition-shadow">
                <span className="text-white font-bold text-sm">{step.num}</span>
              </div>
              <h3 className="font-semibold text-[#0a1628] mb-3 text-sm">{step.title}</h3>
              <p className="text-xs text-slate-500 leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 bg-white rounded-2xl border border-slate-100 p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center">
              <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <h4 className="font-semibold text-[#0a1628]">On-Time Delivery Guarantee</h4>
              <p className="text-sm text-slate-500">We respect your timelines and deliver milestones with precision.</p>
            </div>
          </div>
          <button
            onClick={() => scrollTo('contact')}
            className="flex-shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
          >
            Schedule a Consultation
            <ArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
}

// ─── Pricing ──────────────────────────────────────────────────────────────────
const PLANS = [
  {
    id: 'starter',
    name: 'Essential IT Package',
    price: '$800',
    period: '/ project',
    desc: 'Ideal for small businesses and growing organizations seeking foundational IT services and technology support.',
    popular: false,
    features: [
      'IT Infrastructure Assessment',
      'Network Setup & Configuration',
      'Basic Cybersecurity Review',
      'Cloud Environment Setup',
      'IT Policy Documentation',
      'Technology Recommendations Report',
      '1 Month Direct IT Support',
    ],
    cta: 'Get Started',
  },
  {
    id: 'business',
    name: 'Business IT Solutions',
    price: '$2,500',
    period: '/ project',
    desc: 'Comprehensive IT services and consultancy for growing businesses requiring advanced technology solutions and strategic guidance.',
    popular: true,
    features: [
      'Everything in Essential IT Package',
      'Advanced Security Implementation',
      'Cloud Migration & Optimization',
      'Custom Software Integration',
      'IT Strategy & Roadmap Development',
      'Business Continuity Planning',
      'Priority 24/7 Technical Support',
    ],
    cta: 'Get Started',
  },
  {
    id: 'enterprise',
    name: 'Enterprise IT Consultancy',
    price: 'Custom',
    period: '',
    desc: 'Full-scale IT services and strategic consultancy for enterprise organizations requiring comprehensive technology transformation.',
    popular: false,
    features: [
      'Dedicated Senior IT Consultant',
      'Full Digital Transformation Program',
      'Enterprise Security & Compliance',
      'Multi-Cloud Architecture Design',
      'Custom Software Development',
      'Weekly Strategic Review Calls',
      'Custom SLA & Dedicated Support Manager',
    ],
    cta: 'Contact Us',
  },
];

function PricingSection({ onGetStarted }: { onGetStarted: (plan?: string) => void }) {
  return (
    <section id="pricing" className="py-20 lg:py-28 bg-white" aria-label="Pricing">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
            <span className="w-8 h-px bg-[#0ea5e9]" />
            Pricing Plans
            <span className="w-8 h-px bg-[#0ea5e9]" />
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight">
            Scalable pricing for{' '}
            <span className="text-[#0ea5e9]">every stage</span>{' '}
            of growth.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Select the ideal IT services plan tailored to your organization&apos;s size, complexity, and technology objectives.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-3xl border transition-all duration-300 flex flex-col ${
                plan.popular
                  ? 'border-[#0ea5e9] shadow-[0_8px_40px_rgba(14,165,233,0.15)] scale-[1.02]'
                  : 'border-slate-200 hover:border-[#0ea5e9]/40 hover:shadow-lg'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#0ea5e9] text-white text-xs font-bold rounded-full shadow-lg">
                    <svg className="w-3.5 h-3.5 fill-white" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" /></svg>
                    Most Popular
                  </span>
                </div>
              )}
              <div className={`p-8 rounded-t-3xl ${plan.popular ? 'bg-gradient-to-br from-[#0a1628] to-[#0f2035]' : 'bg-white'}`}>
                <div className="text-sm font-semibold text-[#0ea5e9] mb-3">{plan.name}</div>
                <div className="flex items-end gap-1 mb-3">
                  <span className={`text-4xl font-bold ${plan.popular ? 'text-white' : 'text-[#0a1628]'}`}>{plan.price}</span>
                  {plan.period && <span className={`text-sm mb-1.5 ${plan.popular ? 'text-white/60' : 'text-slate-500'}`}>{plan.period}</span>}
                </div>
                <p className={`text-sm leading-relaxed ${plan.popular ? 'text-white/70' : 'text-slate-500'}`}>{plan.desc}</p>
              </div>
              <div className={`flex-1 p-8 ${plan.popular ? 'bg-[#f8fafc]' : 'bg-white'} rounded-b-3xl`}>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3">
                      <CheckIcon />
                      <span className="text-sm text-slate-600">{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => onGetStarted(plan.id)}
                  className={`w-full flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-full transition-all duration-300 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2 ${
                    plan.popular
                      ? 'bg-[#0ea5e9] text-white hover:bg-[#0369a1] hover:shadow-lg'
                      : 'border-2 border-[#0ea5e9] text-[#0ea5e9] hover:bg-[#0ea5e9] hover:text-white'
                  }`}
                >
                  {plan.cta}
                  <ArrowRight />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Request Modal ────────────────────────────────────────────────────────────
function RequestModal({ isOpen, onClose, initialPlan = '' }: { isOpen: boolean; onClose: () => void; initialPlan?: string }) {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState<RequestFormData>({
    fullName: '', companyName: '', email: '', phone: '',
    selectedPlan: initialPlan, serviceNeeded: '', projectDetails: '', paymentMethod: 'visa',
  });

  useEffect(() => {
    if (isOpen) {
      setForm((p) => ({ ...p, selectedPlan: initialPlan }));
      setSubmitted(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, initialPlan]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Service Request">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-8 py-5 flex items-center justify-between rounded-t-3xl z-10">
          <div>
            <h2 className="text-xl font-bold text-[#0a1628]">Request a Service</h2>
            <p className="text-sm text-slate-500 mt-0.5">Submit your request — no payment required</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9]"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="px-8 py-6">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center mx-auto mb-6">
                <svg className="w-10 h-10 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              </div>
              <h3 className="text-2xl font-bold text-[#0a1628] mb-3">REQUEST RECEIVED</h3>
              <p className="text-slate-600 leading-relaxed max-w-md mx-auto mb-8">
                Thank you. Your request has been received. A QUANTIVA IT representative will review your request and contact you regarding the next steps.
              </p>
              <div className="bg-[#f8fafc] rounded-2xl border border-slate-100 p-4 text-sm text-slate-600 mb-8">
                <strong className="text-[#0a1628]">Important:</strong> No payment has been charged. This is a service request only. Our team will reach out to discuss your requirements and provide a formal proposal.
              </div>
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                {[
                  { id: 'req-fullName', name: 'fullName', label: 'Full Name', type: 'text', placeholder: 'John Smith', required: true },
                  { id: 'req-companyName', name: 'companyName', label: 'Company Name', type: 'text', placeholder: 'Acme Corp', required: false },
                  { id: 'req-email', name: 'email', label: 'Email Address', type: 'email', placeholder: 'john@company.com', required: true },
                  { id: 'req-phone', name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '+1 (555) 000-0000', required: false },
                ].map((f) => (
                  <div key={f.id}>
                    <label htmlFor={f.id} className="block text-sm font-medium text-slate-700 mb-1.5">
                      {f.label} {f.required && <span className="text-red-500">*</span>}
                    </label>
                    <input
                      id={f.id} name={f.name} type={f.type} required={f.required}
                      value={form[f.name as keyof RequestFormData]}
                      onChange={handleChange} placeholder={f.placeholder}
                      className="w-full px-4 py-3 bg-[#f8fafc] border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all"
                    />
                  </div>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label htmlFor="req-selectedPlan" className="block text-sm font-medium text-slate-700 mb-1.5">Selected Plan</label>
                  <select id="req-selectedPlan" name="selectedPlan" value={form.selectedPlan} onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f8fafc] border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all">
                    <option value="">Select a plan</option>
                    <option value="starter">Essential IT Package — $800</option>
                    <option value="business">Business IT Solutions — $2,500</option>
                    <option value="enterprise">Enterprise IT Consultancy — Custom</option>
                  </select>
                </div>
                <div>
                  <label htmlFor="req-serviceNeeded" className="block text-sm font-medium text-slate-700 mb-1.5">Service Needed <span className="text-red-500">*</span></label>
                  <select id="req-serviceNeeded" name="serviceNeeded" required value={form.serviceNeeded} onChange={handleChange}
                    className="w-full px-4 py-3 bg-[#f8fafc] border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all">
                    <option value="">Select a service</option>
                    <option value="infrastructure">IT Infrastructure & Managed Services</option>
                    <option value="cloud">Cloud Solutions & Migration</option>
                    <option value="security">Cybersecurity & Compliance</option>
                    <option value="consulting">Business & IT Consultancy</option>
                    <option value="software">Software Development & Integration</option>
                    <option value="data">Data Management & Analytics</option>
                    <option value="other">Other / Multiple Services</option>
                  </select>
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="req-projectDetails" className="block text-sm font-medium text-slate-700 mb-1.5">Project Details <span className="text-red-500">*</span></label>
                <textarea id="req-projectDetails" name="projectDetails" required rows={4} value={form.projectDetails} onChange={handleChange}
                  placeholder="Please describe your project, current challenges, and what you hope to achieve..."
                  className="w-full px-4 py-3 bg-[#f8fafc] border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all resize-none" />
              </div>

              {/* Payment method (visual only) */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-slate-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.955 11.955 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                  <span className="text-sm font-medium text-slate-700">Preferred Payment Method</span>
                  <span className="text-xs text-slate-400">(for reference only — no payment processed)</span>
                </div>
                <div className="flex flex-wrap gap-2.5">
                  {[
                    { id: 'visa', label: 'Visa' },
                    { id: 'mastercard', label: 'Mastercard' },
                    { id: 'amex', label: 'Amex' },
                    { id: 'discover', label: 'Discover' },
                    { id: 'paypal', label: 'PayPal' },
                  ].map((m) => (
                    <label key={m.id} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border cursor-pointer transition-all ${form.paymentMethod === m.id ? 'border-[#0ea5e9] bg-[#0ea5e9]/5 text-[#0ea5e9]' : 'border-slate-200 bg-white text-slate-600 hover:border-[#0ea5e9]/40'}`}>
                      <input type="radio" name="paymentMethod" value={m.id} checked={form.paymentMethod === m.id} onChange={handleChange} className="sr-only" />
                      <span className="text-sm font-medium">{m.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <p className="text-xs text-amber-800 leading-relaxed">
                  <strong>No payment will be processed.</strong> This form submits a service request only. A QUANTIVA IT representative will contact you to discuss your requirements and provide a formal proposal before any payment is requested.
                </p>
              </div>

              <button type="submit" className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2">
                Submit Request
                <ArrowRight />
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── CTA Section ──────────────────────────────────────────────────────────────
function CTASection({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <section className="py-20 lg:py-28 bg-gradient-to-br from-[#0a1628] via-[#0f2035] to-[#162d4a] relative overflow-hidden" aria-label="Call to action">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#0ea5e9]/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-[#06b6d4]/10 blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
              <span className="w-8 h-px bg-[#0ea5e9]" />
              Ready to Launch?
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight tracking-tight mb-6">
              Ready to transform your{' '}
              <span className="text-[#0ea5e9]">technology</span>?
            </h2>
            <p className="text-white/70 leading-relaxed mb-8">
              Consult with our IT solutions architects today for a tailored proposal designed around your business objectives.
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={onGetStarted}
                className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-xl hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1628]"
              >
                Get Started Today
                <ArrowRight />
              </button>
              <button
                onClick={() => scrollTo('pricing')}
                className="inline-flex items-center gap-2 px-7 py-3.5 border border-white/30 text-white font-semibold rounded-full transition-all duration-300 hover:bg-white/10 hover:border-white/60 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a1628]"
              >
                View Pricing
                <ArrowRight />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: '⚡', title: 'Fast Deployment', desc: 'High-quality IT solutions delivered at the speed of your business.' },
              { icon: '🔒', title: 'Secure & Scalable', desc: 'Enterprise-grade security and scalable architectures by default.' },
              { icon: '🛟', title: '24/7 Support', desc: 'Round-the-clock priority technical support for every client.' },
              { icon: '📊', title: '99% Uptime SLA', desc: 'Guaranteed service availability backed by our SLA commitment.' },
            ].map((item) => (
              <div key={item.title} className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-5 hover:border-[#0ea5e9]/40 hover:bg-white/10 transition-all duration-300">
                <div className="text-2xl mb-3">{item.icon}</div>
                <h4 className="font-semibold text-white text-sm mb-1">{item.title}</h4>
                <p className="text-white/60 text-xs leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Careers ──────────────────────────────────────────────────────────────────
function CareersSection() {
  return (
    <section id="careers" className="py-20 lg:py-28 bg-[#f8fafc]" aria-label="Careers">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
              <span className="w-8 h-px bg-[#0ea5e9]" />
              Careers
            </span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight mb-6">
              Build your career at{' '}
              <span className="text-[#0ea5e9]">QUANTIVA IT</span>.
            </h2>
            <p className="text-slate-600 leading-relaxed mb-5">
              At QUANTIVA IT, we are always looking for talented, driven professionals who are passionate about technology and committed to delivering exceptional results for our clients.
            </p>
            <p className="text-slate-600 leading-relaxed mb-8">
              We offer a collaborative, growth-oriented environment where your expertise is valued, your ideas are heard, and your career can flourish. Whether you are an experienced IT professional or an emerging talent, we want to hear from you.
            </p>
            <div className="space-y-4 mb-8">
              {[
                { title: 'Professional Growth', desc: 'Continuous learning opportunities, certifications, and career development programs.' },
                { title: 'Collaborative Culture', desc: 'Work alongside experienced professionals in a supportive, team-oriented environment.' },
                { title: 'Meaningful Work', desc: 'Solve real business challenges and make a tangible impact for clients across the USA.' },
                { title: 'Competitive Compensation', desc: 'Attractive salary packages, benefits, and performance-based incentives.' },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckIcon />
                  </div>
                  <div>
                    <span className="font-semibold text-[#0a1628] text-sm">{item.title}</span>
                    <span className="text-slate-500 text-sm"> — {item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <a
              href="mailto:quantivait.llc@outlook.com?subject=Career Opportunity Inquiry"
              className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2"
            >
              Explore Opportunities
              <ArrowRight />
            </a>
          </div>

          <div className="space-y-4">
            <div className="bg-white rounded-3xl border border-slate-100 p-8 shadow-sm">
              <h3 className="text-lg font-bold text-[#0a1628] mb-2">Open to Qualified Professionals</h3>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                We are actively seeking experienced professionals across various IT disciplines. If you have expertise in any of the following areas, we encourage you to reach out:
              </p>
              <div className="grid grid-cols-2 gap-3">
                {['IT Infrastructure', 'Cloud Engineering', 'Cybersecurity', 'Software Development', 'IT Project Management', 'Business Analysis', 'Data Engineering', 'IT Consulting'].map((role) => (
                  <div key={role} className="flex items-center gap-2 text-sm text-slate-600">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0ea5e9] flex-shrink-0" />
                    {role}
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#0a1628] to-[#0f2035] rounded-3xl p-8 text-white">
              <div className="w-12 h-12 rounded-2xl bg-[#0ea5e9]/20 flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>
              </div>
              <h4 className="font-bold text-lg mb-2">Send Your Application</h4>
              <p className="text-white/70 text-sm leading-relaxed mb-4">
                Send your resume and a brief introduction to our team. We review every application and will be in touch if there is a suitable opportunity.
              </p>
              <a href="mailto:quantivait.llc@outlook.com?subject=Job Application" className="inline-flex items-center gap-2 text-[#0ea5e9] text-sm font-semibold hover:text-white transition-colors">
                quantivait.llc@outlook.com
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────
function ContactSection() {
  const [form, setForm] = useState<ContactFormData>({ fullName: '', email: '', phone: '', company: '', serviceNeeded: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <section id="contact" className="py-20 lg:py-28 bg-white" aria-label="Contact">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-2 text-xs font-bold text-[#0ea5e9] uppercase tracking-[0.15em] mb-5">
            <span className="w-8 h-px bg-[#0ea5e9]" />
            Contact Us
            <span className="w-8 h-px bg-[#0ea5e9]" />
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0a1628] leading-tight tracking-tight">
            Let&apos;s start a{' '}
            <span className="text-[#0ea5e9]">conversation</span>.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl mx-auto">
            Ready to transform your technology? Get in touch with our team today.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-10 lg:gap-16">
          {/* Contact info */}
          <div className="lg:col-span-2">
            <div className="space-y-6">
              {[
                { label: 'NAME', value: 'MUSA ALHAJI', href: null, icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" /></svg> },
                { label: 'COMPANY', value: 'QUANTIVA IT', href: null, icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h1.5m-1.5 3h1.5m-1.5 3h1.5m3-6H15m-1.5 3H15m-1.5 3H15M9 21v-3.375c0-.621.504-1.125 1.125-1.125h3.75c.621 0 1.125.504 1.125 1.125V21" /></svg> },
                { label: 'MAIL', value: 'quantivait.llc@outlook.com', href: 'mailto:quantivait.llc@outlook.com', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg> },
                { label: 'PHONE', value: '+1 3642173969', href: 'tel:+13642173969', icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" /></svg> },
                { label: 'ADDRESS', value: '110 N Court St\nScottsville, KY 42164\nUSA', href: null, icon: <svg className="w-5 h-5 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" /></svg> },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#0ea5e9]/10 flex items-center justify-center flex-shrink-0">
                    {item.icon}
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#0ea5e9] uppercase tracking-widest mb-1">{item.label}</div>
                    {item.href ? (
                      <a href={item.href} className="text-slate-700 font-medium hover:text-[#0ea5e9] transition-colors break-all">{item.value}</a>
                    ) : (
                      <p className="text-slate-700 font-medium whitespace-pre-line">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contact form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-[#f8fafc] rounded-3xl border border-slate-100 p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#0ea5e9]/10 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-[#0ea5e9]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                </div>
                <h3 className="text-xl font-bold text-[#0a1628] mb-2">Message Sent!</h3>
                <p className="text-slate-600">Thank you for reaching out. A QUANTIVA IT representative will be in touch with you shortly.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-[#f8fafc] rounded-3xl border border-slate-100 p-8" noValidate>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  {[
                    { id: 'con-fullName', name: 'fullName', label: 'Full Name', type: 'text', placeholder: 'John Smith', required: true },
                    { id: 'con-email', name: 'email', label: 'Email Address', type: 'email', placeholder: 'john@company.com', required: true },
                    { id: 'con-phone', name: 'phone', label: 'Phone Number', type: 'tel', placeholder: '+1 (555) 000-0000', required: false },
                    { id: 'con-company', name: 'company', label: 'Company', type: 'text', placeholder: 'Your company', required: false },
                  ].map((f) => (
                    <div key={f.id}>
                      <label htmlFor={f.id} className="block text-sm font-medium text-slate-700 mb-1.5">
                        {f.label} {f.required && <span className="text-red-500">*</span>}
                      </label>
                      <input
                        id={f.id} name={f.name} type={f.type} required={f.required}
                        value={form[f.name as keyof ContactFormData]}
                        onChange={handleChange} placeholder={f.placeholder}
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all"
                      />
                    </div>
                  ))}
                </div>
                <div className="mb-4">
                  <label htmlFor="con-serviceNeeded" className="block text-sm font-medium text-slate-700 mb-1.5">Service Needed</label>
                  <select id="con-serviceNeeded" name="serviceNeeded" value={form.serviceNeeded} onChange={handleChange}
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all">
                    <option value="">Select a service</option>
                    <option value="infrastructure">IT Infrastructure & Managed Services</option>
                    <option value="cloud">Cloud Solutions & Migration</option>
                    <option value="security">Cybersecurity & Compliance</option>
                    <option value="consulting">Business & IT Consultancy</option>
                    <option value="software">Software Development & Integration</option>
                    <option value="data">Data Management & Analytics</option>
                    <option value="other">Other / General Inquiry</option>
                  </select>
                </div>
                <div className="mb-6">
                  <label htmlFor="con-message" className="block text-sm font-medium text-slate-700 mb-1.5">Message <span className="text-red-500">*</span></label>
                  <textarea id="con-message" name="message" required rows={5} value={form.message} onChange={handleChange}
                    placeholder="Tell us about your project or inquiry..."
                    className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#0ea5e9] focus:border-transparent transition-all resize-none" />
                </div>
                <button type="submit" className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-[#0ea5e9] text-white font-semibold rounded-full transition-all duration-300 hover:bg-[#0369a1] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] focus-visible:ring-offset-2">
                  Send Message
                  <ArrowRight />
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function PageFooter({ onGetStarted }: { onGetStarted: () => void }) {
  return (
    <footer className="bg-[#060d1a] text-white" aria-label="Footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#0ea5e9] to-[#0369a1] flex items-center justify-center">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2L15.5 5.5V12.5L9 16L2.5 12.5V5.5L9 2Z" stroke="white" strokeWidth="1.5" strokeLinejoin="round"/>
                  <path d="M9 6L12 7.5V10.5L9 12L6 10.5V7.5L9 6Z" fill="white"/>
                </svg>
              </div>
              <div className="leading-none">
                <span className="block font-bold text-base tracking-tight text-white">QUANTIVA<span className="text-[#0ea5e9]"> IT</span></span>
                <span className="block text-[9px] font-semibold tracking-[0.18em] text-white/40 uppercase mt-0.5">IT Services</span>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed mb-6">
              QUANTIVA IT is a professional U.S.-based IT services and business &amp; IT consultancy company delivering high-impact technology solutions for organizations across America.
            </p>
            <div>
              <div className="text-xs font-bold text-white/30 uppercase tracking-widest mb-3">Secure Payments</div>
              <div className="flex flex-wrap gap-2">
                {['Visa', 'Mastercard', 'Amex', 'Discover', 'PayPal'].map((m) => (
                  <span key={m} className="px-3 py-1.5 bg-white/8 rounded-lg text-xs font-medium text-white/60 border border-white/10">{m}</span>
                ))}
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-4">Navigation</h4>
            <ul className="space-y-3">
              {[
                { label: 'Home', id: 'hero' },
                { label: 'About', id: 'about' },
                { label: 'Services', id: 'services' },
                { label: 'Pricing', id: 'pricing' },
                { label: 'Careers', id: 'careers' },
                { label: 'Contact', id: 'contact' },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollTo(link.id)}
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-[#0ea5e9] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] rounded"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#0ea5e9]/50" />
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-4">Services</h4>
            <ul className="space-y-3">
              {['IT Infrastructure', 'Cloud Solutions', 'Cybersecurity', 'IT Consulting', 'Software Development', 'Data Management'].map((s) => (
                <li key={s}>
                  <button
                    onClick={() => scrollTo('services')}
                    className="flex items-center gap-2 text-sm text-white/60 hover:text-[#0ea5e9] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0ea5e9] rounded"
                  >
                    <span className="w-1 h-1 rounded-full bg-[#0ea5e9]/50" />
                    {s}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white text-sm mb-4">Contact</h4>
            <ul className="space-y-4">
              <li>
                <div className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1">Contact Person</div>
                <div className="text-sm text-white/70">MUSA ALHAJI</div>
              </li>
              <li>
                <div className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1">Email</div>
                <a href="mailto:quantivait.llc@outlook.com" className="text-sm text-white/70 hover:text-[#0ea5e9] transition-colors break-all">quantivait.llc@outlook.com</a>
              </li>
              <li>
                <div className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1">Phone</div>
                <a href="tel:+13642173969" className="text-sm text-white/70 hover:text-[#0ea5e9] transition-colors">+1 3642173969</a>
              </li>
              <li>
                <div className="text-xs font-bold text-white/30 uppercase tracking-widest mb-1">Address</div>
                <address className="text-sm text-white/70 not-italic leading-relaxed">
                  110 N Court St<br />
                  Scottsville, KY 42164<br />
                  USA
                </address>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-white/40">&copy; 2026 QUANTIVA IT. All rights reserved.</p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs text-white/40">Systems Operational</span>
            <span className="text-white/20 mx-2">·</span>
            <span className="text-xs text-white/40">IT Services &amp; Business Consultancy</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function HomePage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('');

  const openModal = useCallback((plan?: string) => {
    setSelectedPlan(plan || '');
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  return (
    <>
      <Navbar onGetStarted={() => openModal()} />
      <main>
        <HeroSection onGetStarted={() => openModal()} />
        <AboutSection />
        <ServicesSection onGetStarted={() => openModal()} />
        <WhyUsSection onGetStarted={() => openModal()} />
        <ProcessSection onGetStarted={() => openModal()} />
        <PricingSection onGetStarted={openModal} />
        <CTASection onGetStarted={() => openModal()} />
        <CareersSection />
        <ContactSection />
      </main>
      <PageFooter onGetStarted={() => openModal()} />
      <RequestModal isOpen={modalOpen} onClose={closeModal} initialPlan={selectedPlan} />
    </>
  );
}
